// Worker API layer (docs/worker.md in the product repository, section "Site consumption").
// base(): "" for same origin (the Vercel rewrite of /api/data/*), else window.BELLGUARD_API_BASE, else the
// ?api= query parameter, persisted in localStorage["bellguard_api"]. ?api=off clears the stored value and
// forces the recorded fallback for this page load.
// get(path, ms): GET base()+path as JSON with a timeout (default 6000 ms), one retry on a network error,
// timeout or 5xx; rejects on any non-2xx answer.

const KEY = 'bellguard_api';
export const DEFAULT_MS = 6000;

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number | null = null,
    public readonly timeout = false,
    public readonly off = false,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const isNotFound = (e: unknown): boolean => e instanceof ApiError && e.status === 404;
export const isOffError = (e: unknown): boolean => e instanceof ApiError && e.off;
export const offError = (): ApiError => new ApiError('API disabled (?api=off)', null, false, true);

let cachedBase: string | null = null;

function readOverride(): string | null {
  let q: string | null = null;
  try {
    q = new URLSearchParams(location.search).get('api');
  } catch {
    q = null;
  }
  try {
    if (q === null) return localStorage.getItem(KEY);
    if (q === 'off') { localStorage.removeItem(KEY); return 'off'; }
    if (q === '') { localStorage.removeItem(KEY); return null; }
    localStorage.setItem(KEY, q);
    return q;
  } catch {
    return q === null || q === '' ? null : q;
  }
}

export function base(): string {
  if (cachedBase !== null) return cachedBase;
  let b = typeof window !== 'undefined' && typeof window.BELLGUARD_API_BASE === 'string' ? window.BELLGUARD_API_BASE : (readOverride() ?? '');
  if (b !== 'off') b = b.replace(/\/+$/, '');
  cachedBase = b;
  return b;
}

export const isOff = (): boolean => base() === 'off';
/** True when the worker API may be asked (not disabled with ?api=off). */
export const apiOn = (): boolean => !isOff();

async function once(url: string, ms: number): Promise<unknown> {
  const ctl = new AbortController();
  const timer = setTimeout(() => { ctl.abort(); }, ms);
  try {
    const r = await fetch(url, { signal: ctl.signal, headers: { accept: 'application/json' } });
    if (!r.ok) throw new ApiError(`HTTP ${r.status}`, r.status);
    return (await r.json()) as unknown;
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw new ApiError(`no answer within ${ms / 1000} s`, null, true);
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

/** GET a JSON payload. The caller names the type; the payload is trusted to match the vendored contract. */
export async function get<T>(path: string, ms: number = DEFAULT_MS): Promise<T> {
  if (isOff()) throw offError();
  const url = base() + path;
  try {
    return (await once(url, ms)) as T;
  } catch (e) {
    if (e instanceof ApiError && e.status !== null && e.status < 500) throw e; // 4xx: the answer is final
    return (await once(url, ms)) as T; // network error, timeout or 5xx: one retry
  }
}
